import { common, query } from '@kurtosys/ksys-app-template';
import { IConfigurationComponentOrchestration } from '@kurtosys/types/app/index.js';
import { IManifest } from '@kurtosys/types/appsManager/IManifest.js';
import { IExecutionOptions, IQueryOptions } from '@kurtosys/types/query/index.js';
import FileSaver from 'file-saver';
import { getExtension } from 'mime';
import { observable } from 'mobx';

import { IComponentStylesBase } from '../../models/IComponentStylesBase.js';
import { IOverridingStoreOptions } from '../../models/IOverridingStoreOptions.js';
import { QueryStore } from '../QueryStore.js';
import { StoreContext } from '../StoreContext.js';
import { TranslationStore } from '../TranslationStore.js';

import { LoadingStoreBase } from './LoadingStoreBase.js';

export abstract class StoreBase<
	TConfiguration,
	TComponentStyles extends IComponentStylesBase,
> extends LoadingStoreBase {
	storeContext: StoreContext<TConfiguration, TComponentStyles, any, any, any>;
	manifest: IManifest;
	@observable isInitialized = false;
	constructor(storeContext: StoreContext<TConfiguration, TComponentStyles, any, any, any>, manifest: IManifest) {
		super();
		this.storeContext = storeContext;
		this.manifest = manifest;
	}
	abstract initialize(callbacks: Record<string, EventListener> | undefined): Promise<void>;

	translate(value?: string, placeholders?: any): string | undefined {
		const translationStore =
			this.storeContext.get<TranslationStore<TConfiguration, TComponentStyles>>('translationStore');
		if (translationStore && typeof value === 'string') {
			return translationStore.translate(value, placeholders);
		}
		return value;
	}

	getQueryValue(
		queryOptions?: IQueryOptions,
		responseForContext?: object,
		overrideExecutionOptions?: Partial<IExecutionOptions>,
		overridingOptions: IOverridingStoreOptions<TConfiguration, TComponentStyles> = {},
	) {
		if (queryOptions) {
			const queryStore = this.getQueryStore(overridingOptions);
			const responseContextExecutionOptions: Partial<IExecutionOptions> = {
				responseContext: responseForContext,
			};
			overrideExecutionOptions = common.commonUtils.deepMergeObjectsWithOptions(
				{ arrayMergeStrategy: 'DeepMerge' },
				responseContextExecutionOptions,
				overrideExecutionOptions || {},
			);
			return queryStore.query(queryOptions, overrideExecutionOptions);
		}
		return;
	}

	mergeQueriesAndProps<T>(
		componentOrchestration?: IConfigurationComponentOrchestration,
		responseContext?: any,
		overridingOptions: IOverridingStoreOptions<TConfiguration, TComponentStyles> = {},
	): T | undefined {
		if (componentOrchestration) {
			const queryStore = this.getQueryStore(overridingOptions);
			const translationStore =
				this.storeContext.get<TranslationStore<TConfiguration, TComponentStyles>>('translationStore');
			const { props, queries } = componentOrchestration;
			const translationHelper = translationStore && translationStore.translationHelper;
			let workingExecutionOptions = queryStore.executionOptions;
			if (responseContext) {
				workingExecutionOptions = {
					...workingExecutionOptions,
					responseContext,
				};
			}
			const queryResults = queries
				? query.queryUtils.queryHelper.convertQueryMime(
						{ queryClass: query.Query },
						queries,
						workingExecutionOptions,
						{
							translationHelper,
						},
				  )
				: {};
			const response: T = common.commonUtils.deepMergeObjectsWithOptions(
				{ arrayMergeStrategy: 'DeepMerge' },
				props || {},
				queryResults,
			);
			return response;
		}
	}

	getQueryStore(
		overridingOptions: IOverridingStoreOptions<TConfiguration, TComponentStyles>,
	): QueryStore<TConfiguration, TComponentStyles> {
		const queryStore = this.storeContext.get<QueryStore<TConfiguration, TComponentStyles>>('queryStore');
		const { queryStore: queryStoreOverride } = overridingOptions;
		return queryStoreOverride || queryStore;
	}

	saveFile = (blob: any, filename: string) => {
		const extension = getExtension(blob.type);
		const fullFileName = extension ? `${filename}.${extension}` : filename;
		FileSaver.saveAs(blob, fullFileName);
		// Returning the file name for analytics logging
		return fullFileName;
	};
}
